/**
 * sales-boost.js — Livostyle sales conversion toolkit
 * Modules: wishlist, sticky ATC, recently viewed, free-ship bar,
 *          size guide modal, cart upsell, bundle tiers.
 * Deferred-loaded (scripts are `defer` in theme.liquid).
 * No external deps. Vanilla ES2017+.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------
   * 0. Shared utilities
   * --------------------------------------------------------- */
  var LS = window.localStorage;
  var hasLS = (function () {
    try { LS.setItem('__t', '1'); LS.removeItem('__t'); return true; }
    catch (e) { return false; }
  })();
  function readLS(key, fallback) {
    if (!hasLS) return fallback;
    try { var v = LS.getItem(key); return v ? JSON.parse(v) : fallback; }
    catch (e) { return fallback; }
  }
  function writeLS(key, value) {
    if (!hasLS) return;
    try { LS.setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function formatMoney(cents, currency) {
    // Works with Shopify money cents. Currency defaults to USD.
    var code = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
  function fireEvent(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail: detail || {}, bubbles: true }));
  }

  /* -----------------------------------------------------------
   * 1. Wishlist module
   * --------------------------------------------------------- */
  var WL_KEY = 'livostyle_wishlist';
  var Wishlist = {
    get: function () { return readLS(WL_KEY, []); },
    has: function (handle) { return this.get().indexOf(handle) !== -1; },
    add: function (handle) {
      if (!handle) return;
      var list = this.get();
      if (list.indexOf(handle) === -1) { list.unshift(handle); writeLS(WL_KEY, list); }
      fireEvent('wishlist:change', { handle: handle, action: 'add', count: list.length });
    },
    remove: function (handle) {
      var list = this.get().filter(function (h) { return h !== handle; });
      writeLS(WL_KEY, list);
      fireEvent('wishlist:change', { handle: handle, action: 'remove', count: list.length });
    },
    toggle: function (handle) {
      if (this.has(handle)) this.remove(handle);
      else this.add(handle);
      return this.has(handle);
    },
    count: function () { return this.get().length; }
  };
  window.LivostyleWishlist = Wishlist;

  function bindWishlistButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('[data-wishlist-btn]');
    buttons.forEach(function (btn) {
      if (btn.__wlBound) return;
      btn.__wlBound = true;
      var handle = btn.getAttribute('data-wishlist-handle');
      if (Wishlist.has(handle)) btn.classList.add('is-active');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var active = Wishlist.toggle(handle);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active) btn.classList.add('sb-burst');
        setTimeout(function () { btn.classList.remove('sb-burst'); }, 600);
      });
    });
  }

  function updateWishlistBadge() {
    var n = Wishlist.count();
    // Heart buttons / per-card counters (integer only)
    document.querySelectorAll('[data-wishlist-count]:not([data-wishlist-page] [data-wishlist-count])').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-visible', n > 0);
    });
    // Header bubble (visibility toggle + numeric)
    document.querySelectorAll('[data-wishlist-header-count]').forEach(function (el) {
      var inner = el.querySelector('span[aria-hidden="true"]') || el;
      inner.textContent = n > 99 ? '99+' : n;
      if (n > 0) { el.removeAttribute('hidden'); }
      else { el.setAttribute('hidden', ''); }
    });
  }

  document.addEventListener('wishlist:change', updateWishlistBadge);

  /* -----------------------------------------------------------
   * 2. Recently viewed tracker
   * --------------------------------------------------------- */
  var RV_KEY = 'livostyle_recently_viewed';
  var RV_MAX = 12;
  var RecentlyViewed = {
    get: function () { return readLS(RV_KEY, []); },
    add: function (handle) {
      if (!handle) return;
      var list = this.get().filter(function (h) { return h !== handle; });
      list.unshift(handle);
      if (list.length > RV_MAX) list = list.slice(0, RV_MAX);
      writeLS(RV_KEY, list);
    }
  };
  window.LivostyleRecentlyViewed = RecentlyViewed;

  // Auto-track on PDP via meta tag
  var pdpMeta = document.querySelector('meta[name="product-handle"]');
  if (pdpMeta) RecentlyViewed.add(pdpMeta.getAttribute('content'));

  /* -----------------------------------------------------------
   * 3. Free-ship progress bar
   * --------------------------------------------------------- */
  var FREE_SHIP_THRESHOLD_CENTS = (function () {
    var meta = document.querySelector('meta[name="livostyle-freeship-threshold"]');
    return meta ? parseInt(meta.getAttribute('content'), 10) : 9700;
  })();
  window.LivostyleFreeShipThreshold = FREE_SHIP_THRESHOLD_CENTS;

  function renderFreeShipBar(cartTotalCents) {
    var bars = document.querySelectorAll('[data-freeship-bar]');
    if (!bars.length) return;
    var total = Math.max(0, cartTotalCents || 0);
    var remaining = Math.max(0, FREE_SHIP_THRESHOLD_CENTS - total);
    var pct = Math.min(100, Math.round((total / FREE_SHIP_THRESHOLD_CENTS) * 100));

    bars.forEach(function (bar) {
      var fill = bar.querySelector('[data-freeship-fill]');
      var msg = bar.querySelector('[data-freeship-msg]');
      if (fill) fill.style.width = pct + '%';
      if (msg) {
        if (remaining === 0) {
          bar.classList.add('is-complete');
          msg.innerHTML = 'Congrats! You qualify for <strong>FREE shipping</strong>';
        } else {
          bar.classList.remove('is-complete');
          msg.innerHTML = 'Add <strong>' + formatMoney(remaining) + '</strong> more to unlock <strong>FREE shipping</strong>';
        }
      }
    });
  }
  window.LivostyleRenderFreeShipBar = renderFreeShipBar;

  function refreshCartUI() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        renderFreeShipBar(cart.total_price);
        fireEvent('cart:updated', { cart: cart });
      })
      .catch(function () {});
  }
  window.LivostyleRefreshCart = refreshCartUI;

  /* -----------------------------------------------------------
   * 4. Sticky mobile ATC
   * --------------------------------------------------------- */
  function initStickyATC() {
    var sticky = document.querySelector('[data-sticky-atc]');
    if (!sticky) return;
    var mainCta = document.querySelector('product-form button[type="submit"], .product-form__buttons button[type="submit"]');
    if (!mainCta) { sticky.hidden = true; return; }

    // Show sticky only when main CTA is out of view
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting || entry.boundingClientRect.top > 0) {
          sticky.classList.remove('is-visible');
        } else {
          sticky.classList.add('is-visible');
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -40% 0px' });
    io.observe(mainCta);

    // Mirror click to main CTA
    var stickyBtn = sticky.querySelector('[data-sticky-atc-btn]');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (mainCta.disabled) return;
        mainCta.click();
      });
    }

    // Mirror variant state (disabled / sold out text)
    var sync = function () {
      if (!stickyBtn) return;
      stickyBtn.disabled = !!mainCta.disabled;
      var priceEl = document.querySelector('.price__container .price-item--sale, .price__container .price-item--regular');
      var priceSticky = sticky.querySelector('[data-sticky-atc-price]');
      if (priceSticky && priceEl) priceSticky.textContent = priceEl.textContent.trim();
    };
    sync();
    var mo = new MutationObserver(sync);
    mo.observe(mainCta, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    document.addEventListener('variant:change', sync);
  }

  /* -----------------------------------------------------------
   * 5. Size guide modal
   * --------------------------------------------------------- */
  function initSizeGuideModal() {
    var modal = document.querySelector('[data-size-guide-modal]');
    if (!modal) return;
    var openBtns = document.querySelectorAll('[data-size-guide-open]');
    var closeBtns = modal.querySelectorAll('[data-size-guide-close]');
    var tabs = modal.querySelectorAll('[data-size-tab]');
    var panels = modal.querySelectorAll('[data-size-panel]');
    var lastFocus = null;

    function open(e) {
      if (e) e.preventDefault();
      lastFocus = document.activeElement;
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      document.body.style.overflow = 'hidden';
      var firstBtn = modal.querySelector('[data-size-guide-close]');
      if (firstBtn) firstBtn.focus();
      document.addEventListener('keydown', onEsc);
    }
    function close() {
      modal.classList.remove('is-open');
      setTimeout(function () { modal.hidden = true; }, 220);
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onEsc);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }
    function onEsc(e) { if (e.key === 'Escape') close(); }

    openBtns.forEach(function (b) { b.addEventListener('click', open); });
    closeBtns.forEach(function (b) { b.addEventListener('click', close); });
    modal.addEventListener('click', function (e) { if (e.target === modal) close(); });

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-size-tab');
        tabs.forEach(function (t) { t.classList.toggle('is-active', t === tab); });
        panels.forEach(function (p) {
          p.hidden = p.getAttribute('data-size-panel') !== target;
        });
      });
    });
  }

  /* -----------------------------------------------------------
   * 6. Bundle tier selector (for PDP custom block)
   * --------------------------------------------------------- */
  function initBundleTiers() {
    var wrap = document.querySelector('[data-bundle-tiers]');
    if (!wrap) return;
    var tiers = wrap.querySelectorAll('[data-bundle-tier]');
    var qtyInput = document.querySelector('product-form .quantity__input, input[name="quantity"]');
    tiers.forEach(function (tier) {
      tier.addEventListener('click', function () {
        var qty = parseInt(tier.getAttribute('data-bundle-tier'), 10) || 1;
        tiers.forEach(function (t) { t.classList.toggle('is-selected', t === tier); });
        if (qtyInput) {
          qtyInput.value = qty;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  /* -----------------------------------------------------------
   * 7. Cart upsell — quick-add from drawer
   * --------------------------------------------------------- */
  function initCartUpsell() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      e.preventDefault();
      var vid = btn.getAttribute('data-upsell-variant');
      if (!vid) return;
      btn.disabled = true;
      btn.classList.add('is-loading');
      var original = btn.textContent;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          btn.textContent = 'Added';
          refreshCartUI();
          // Reopen cart drawer to show added state
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function') {
            fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
              .then(function (r) { return r.json(); })
              .then(function (sections) {
                if (drawer.getSectionsToRender) {
                  drawer.renderContents({ sections: sections, items: [{}] });
                }
              })
              .catch(function () { window.location.reload(); });
          }
        })
        .catch(function () { btn.textContent = 'Try again'; })
        .finally(function () {
          setTimeout(function () {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.textContent = original;
          }, 1500);
        });
    });
  }

  /* -----------------------------------------------------------
   * 8. Urgency: sale countdown (ends-today banner)
   * --------------------------------------------------------- */
  function initCountdowns() {
    var els = document.querySelectorAll('[data-countdown-end-of-day]');
    if (!els.length) return;
    function tick() {
      var now = new Date();
      var end = new Date();
      end.setHours(23, 59, 59, 999);
      var diff = Math.max(0, end - now);
      var h = Math.floor(diff / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      var text = (h < 10 ? '0' + h : h) + ':' + (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
      els.forEach(function (el) { el.textContent = text; });
    }
    tick();
    setInterval(tick, 1000);
  }

  /* -----------------------------------------------------------
   * 9. Init
   * --------------------------------------------------------- */
  function init() {
    bindWishlistButtons();
    updateWishlistBadge();
    initStickyATC();
    initSizeGuideModal();
    initBundleTiers();
    initCartUpsell();
    initCountdowns();
    refreshCartUI();

    // Re-bind wishlist on dynamic DOM changes (cart drawer, quick add, etc.)
    document.addEventListener('cart:updated', function () {
      bindWishlistButtons();
      updateWishlistBadge();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
